import React, { useContext, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Modal,
  Snackbar,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import Picker from 'react-month-picker';
import { format, parse } from 'date-fns';
import xlsx from 'json-as-xlsx';
import Dropzone from 'react-dropzone';
import Papa from 'papaparse';
import { useExcelParser } from '../../Helpers/useExcelParser';

// Icons
import DownloadIcon from '@mui/icons-material/Download';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Custom Components
import Navigation from '../../Components/Navigation/navigation';
import './newProject.scss';
import { useAuth } from '../../Contexts/AuthContext';
import { useSimulation } from '../../Contexts/SimulationContext';
import DataContext from '../../Contexts/DataContext';
import { getMonthsBetweenDates } from '../../Helpers/utils';
import { validateProjectData } from '../../Helpers/validation';
import { createProjectAsync } from '../../Helpers/dataApi';
import { parseChangesSheet, parseReferenceAccountsSheet } from '../../Helpers/excelImport';
import ChangeProjectTitleModal from '../../Components/ChangeProjectTitleModal/changeProjectTitleModal';
import SimulationBanner from '../../Components/SimulationBanner/SimulationBanner';


/**
 * Normalises a column header string to 'MMM yyyy' format.
 * Supports: 'MMM yyyy' / 'MMMM yyyy' (e.g. "Jan 2024" / "January 2024"),
 *           'MM/yyyy' (e.g. "01/2024"), and 'yyyy-MM-dd' (e.g. "2024-01-01").
 * Returns the original string unchanged if no pattern matches.
 */
const normalizeDateCol = (dateStr) => {
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'MMM yyyy');
    }
    if (/^[A-Za-z]{3,9}\s+\d{4}$/.test(dateStr)) {
      const parts = dateStr.trim().split(/\s+/);
      // Try abbreviated (MMM) and full (MMMM) month name formats
      const dateAbbrev = parse(`${parts[0]} ${parts[1]}`, 'MMM yyyy', new Date());
      const dateFull = parse(`${parts[0]} ${parts[1]}`, 'MMMM yyyy', new Date());
      const d = !isNaN(dateAbbrev.getTime()) ? dateAbbrev : dateFull;
      if (!isNaN(d.getTime())) return format(d, 'MMM yyyy');
    }
    if (/^\d{1,2}\/\d{4}$/.test(dateStr)) {
      const [month, year] = dateStr.split('/');
      const d = parse(`${month.padStart(2, '0')}/${year}`, 'MM/yyyy', new Date());
      if (!isNaN(d.getTime())) return format(d, 'MMM yyyy');
    }
    return dateStr;
  } catch (e) { return dateStr; }
};

// Fixed non-date column names that should never be treated as month columns
const FIXED_ACCOUNT_COLUMNS = new Set([
  'Entity ID', 'Entity', 'Department ID', 'Department Name',
  'Account ID', 'Account Name', 'Parent Account', 'Acct Type',
  'Fixed or Variable', 'Driver',
]);

/**
 * Scans the first sheet of parsedData for month-column headers, normalises
 * them to 'MMM yyyy', and returns the full ordered month list.
 *
 * @param {Object} parsedData - keyed by sheet name; values are arrays of row objects
 * @returns {{ dateRange: string[], error: string|null }}
 *   dateRange: ordered 'MMM yyyy' list (empty on error)
 *   error: human-readable message, or null on success
 */
const autoDetectDateRange = (parsedData, getMonthsBetween) => {
  const sheetKeys = Object.keys(parsedData || {});
  if (sheetKeys.length === 0) {
    return { dateRange: [], error: 'The file appears to be empty or has no data rows.' };
  }

  const rows = parsedData[sheetKeys[0]];
  if (!rows || rows.length === 0) {
    return { dateRange: [], error: 'The file appears to be empty or has no data rows.' };
  }

  // Collect all header keys from the first data row
  const headers = Object.keys(rows[0]);

  // Filter out fixed non-date columns, normalise the rest, keep only valid 'MMM yyyy' results
  const MMM_YYYY_RE = /^[A-Za-z]{3} \d{4}$/;
  const normalised = headers
    .filter(h => !FIXED_ACCOUNT_COLUMNS.has(h))
    .map(h => normalizeDateCol(h))
    .filter(h => MMM_YYYY_RE.test(h));

  // Deduplicate
  const unique = [...new Set(normalised)];

  if (unique.length === 0) {
    return {
      dateRange: [],
      error: 'No date columns were found in your file. Column headers should be in one of these formats: "Jan 2024", "January 2024", "01/2024", or "2024-01-01".',
    };
  }

  // Sort chronologically
  unique.sort((a, b) => {
    const da = parse(a, 'MMM yyyy', new Date());
    const db = parse(b, 'MMM yyyy', new Date());
    return da.getTime() - db.getTime();
  });

  if (unique.length === 1) {
    return {
      dateRange: [],
      error: `Only one date column was detected ("${unique[0]}"). A project requires at least two months.`,
    };
  }

  const start = unique[0];
  const end = unique[unique.length - 1];

  if (start === end) {
    return {
      dateRange: [],
      error: `The detected date range spans only one month ("${start}"). At least two distinct months are required.`,
    };
  }

  if (typeof getMonthsBetween !== 'function') {
    return { dateRange: [], error: 'Internal error: date range utility not provided.' };
  }
  const dateRange = getMonthsBetween(start, end) || [];
  return { dateRange, error: null };
};

/**
 * Generates and downloads a single-sheet blank template (.xlsx).
 * The "Data" sheet contains the 10 fixed account columns followed by
 * one column per month in the provided dateRange.
 */
const createSingleSheetTemplate = (dateRange) => {
  const columns = [
    { label: 'Entity ID',         value: 'Entity ID' },
    { label: 'Entity',            value: 'Entity' },
    { label: 'Department ID',     value: 'Department ID' },
    { label: 'Department Name',   value: 'Department Name' },
    { label: 'Account ID',        value: 'Account ID' },
    { label: 'Account Name',      value: 'Account Name' },
    { label: 'Parent Account',    value: 'Parent Account' },
    { label: 'Acct Type',         value: 'Acct Type' },
    { label: 'Fixed or Variable', value: 'Fixed or Variable' },
    { label: 'Driver',            value: 'Driver' },
    ...dateRange.map(d => ({ label: d, value: d })),
  ];
  xlsx(
    [{ sheet: 'Data', columns, content: [{}] }],
    { fileName: 'base data template', extraLength: 3, writeOptions: {} }
  );
};

const PICKER_LANG = {
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  from: 'From', to: 'To',
};
const PICKER_RANGE = { min: { year: 1970, month: 1 }, max: { year: 2100, month: 12 } };
const makePickerText = (m) => (m && m.year && m.month ? `${PICKER_LANG.months[m.month - 1]}. ${m.year}` : '?');

/**
 * Modal dialog for selecting a date range and downloading a blank template.
 * Owns its own date-picker state — never writes to the parent component's state.
 */
function TemplateDownloadModal({ open, onClose }) {
  const defaultRange = { from: { year: new Date().getFullYear(), month: 1 }, to: { year: new Date().getFullYear(), month: 12 } };
  const [modalMonthRangeValue, setModalMonthRangeValue] = React.useState(defaultRange);
  const [modalDateRange, setModalDateRange] = React.useState([]);
  const pickerRef = React.useRef();

  // Reset state whenever the modal opens
  React.useEffect(() => {
    if (open) {
      setModalMonthRangeValue(defaultRange);
      setModalDateRange([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePickerDismiss = (value) => {
    if (!value || !value.from || !value.to) return;
    const start = `${value.from.year}/${String(value.from.month).padStart(2, '0')}`;
    const end   = `${value.to.year}/${String(value.to.month).padStart(2, '0')}`;
    const startDate = parse(start, 'yyyy/MM', new Date());
    const endDate   = parse(end,   'yyyy/MM', new Date());
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;
    const months = [];
    let cur = startDate;
    while (cur <= endDate) {
      months.push(format(cur, 'MMM yyyy'));
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    setModalMonthRangeValue(value);
    setModalDateRange(months);
  };

  const handleDownload = () => {
    createSingleSheetTemplate(modalDateRange);
    onClose();
  };

  const modalStyle = {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420, bgcolor: 'background.paper',
    borderRadius: 3, boxShadow: 24, p: 4,
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="template-modal-title">
      <Box sx={modalStyle}>
        <Typography id="template-modal-title" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Download blank template
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the date range for your project. The template will include one column per month.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Date range
          </Typography>
          <Picker
            ref={pickerRef}
            years={PICKER_RANGE}
            value={modalMonthRangeValue}
            lang={PICKER_LANG}
            onChange={() => {}}
            onDismiss={handlePickerDismiss}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => pickerRef.current && pickerRef.current.show()}
              sx={{ textTransform: 'none' }}
            >
              {makePickerText(modalMonthRangeValue.from)} – {makePickerText(modalMonthRangeValue.to)}
            </Button>
          </Picker>
          {modalDateRange.length >= 2 && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
              {modalDateRange.length} months selected ({modalDateRange[0]} – {modalDateRange[modalDateRange.length - 1]})
            </Typography>
          )}
          {modalDateRange.length === 1 && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              Select at least two months.
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={modalDateRange.length < 2}
          >
            Download
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

function NewProject(props) {

  const auth = useAuth();
  const simulation = useSimulation();
  const context = useContext(DataContext);
  const history = useHistory();

  const [state, setState] = useState({
    projectTitle: '',
  });
  const [fileData, setFileData] = useState({
    file: false,
    data: '',
  });
  const [errors, setErrors] = useState({
    fileUpload: null,
    templateUpload: false,
    validationErrors: [],
    validationWarnings: [],
  });
  const [showWarnings, setShowWarnings] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState({
    errors: [],
    warnings: [],
    infos: [],
    isValid: true,
  });
  const [warningsExpanded, setWarningsExpanded] = useState(true);
  const [expandedErrorSections, setExpandedErrorSections] = useState({
    relationship: false,
    consistency: false,
    general: false,
  });
  const [openChangeProjectTitleModal, setOpenChangeProjectTitleModal] = useState(false);
  const [projectExists, setProjectExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [createdProjectTitle, setCreatedProjectTitle] = useState('');
  const [currentJobId, setCurrentJobId] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [detectedDateRange, setDetectedDateRange] = useState([]);
  const [titleTouched, setTitleTouched] = useState(false);
  const navigationTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const titleFieldRef = useRef(null);
  const { parseFile: parseExcelFile, progress: parseProgress, setProgress: setParseProgress, isLoading: isParsing, error: parseError, cancel: cancelParsing, startCreep, stopCreep } = useExcelParser();

  // Constants for error display
  const MAX_DISPLAYED_ERRORS = 10;

  // Memoize filtered error arrays using single-pass reduce for optimal performance
  const filteredErrors = useMemo(() => {
    const validationErrors = errors.validationErrors || [];
    return validationErrors.reduce((acc, err) => {
      if (err.type === 'dataset') {
        acc.dataset.push(err);
      } else if (['Parent Account', 'Driver'].includes(err.field)) {
        acc.relationship.push(err);
      } else if (['Entity', 'Department Name', 'Account Name'].includes(err.field)) {
        acc.consistency.push(err);
      } else {
        acc.general.push(err);
      }
      return acc;
    }, { dataset: [], relationship: [], consistency: [], general: [] });
  }, [errors.validationErrors]);

  // Show title error when: field has been touched (or a file was uploaded) and title is empty
  const titleError = (titleTouched || !!fileData.file) && !state.projectTitle.trim();

  // Computed value: Check if data quality validation passed
  const hasPassedDataQuality = useMemo(() => {
    return !errors.validationErrors?.length && 
           !errors.validationWarnings?.length && 
           fileData.file && 
           validationResults?.isValid;
  }, [errors.validationErrors, errors.validationWarnings, fileData.file, validationResults]);

  // Cleanup navigation timeout on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const clearErrors = () => {
    setErrors({
      fileUpload: null,
      templateUpload: false,
      validationErrors: [],
      validationWarnings: [],
    });
  };

  // Helper function to toggle section expansion
  const toggleErrorSection = (section) => {
    setExpandedErrorSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleChange = (e) => {
      let target = e.target.id;
      let value = e.target.value;

      if (target === 'projectTitle') {
          setState({
            ...state,
            projectTitle: value,
          })
      }
  };

  /**
   * Parse a CSV file into a single-sheet object { Data: [...rows] }.
   * Date range and project title are no longer synthesised here — they are
   * auto-detected from column headers and entered via the title field.
   */
  const parseCsvFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      setParseProgress({ stage: 'loading', percent: 10, message: `Loading ${file.name}...` });
      Papa.parse(file, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (result) => {
          const rawData = result.data;
          const filteredData = rawData.filter(row =>
            Object.values(row).some(value => String(value).trim() !== '')
          );

          if (filteredData.length === 0) {
            reject(new Error('The CSV file appears to be empty or has no data rows.'));
            return;
          }

          setParseProgress({ stage: 'complete', percent: 100, message: 'CSV parsed successfully' });
          resolve({ Data: filteredData });
        },
        error: (err) => {
          reject(new Error(err.message || 'Failed to parse CSV file'));
        },
      });
    });
  }, [setParseProgress]);

  const handleFileUpload = async (fileObjs) => {

      // If no title yet, auto-focus the title field to prompt the user
      if (!state.projectTitle.trim()) {
        setTitleTouched(true);
        if (titleFieldRef.current) {
          titleFieldRef.current.focus();
        }
      }

      // Clear any previous validation results and errors
      setValidationResults(null);
      setDetectedDateRange([]);
      setErrors({
        fileUpload: null,
        templateUpload: false,
        validationErrors: [],
        validationWarnings: [],
      });

      try {
        const file = fileObjs[0];
        const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');

        const data = isCsv ? await parseCsvFile(file) : await parseExcelFile(file);
        console.log('[ExcelParser] Parse complete. Sheets:', Object.keys(data), 'Sheet count:', Object.keys(data).length);
        Object.keys(data).forEach(sheet => {
          console.log(`[ExcelParser] Sheet "${sheet}": ${Array.isArray(data[sheet]) ? data[sheet].length + ' rows' : typeof data[sheet]}`);
        });

        // Use only the first sheet as the Data sheet (single-sheet files and
        // multi-sheet files are both handled the same way now)
        const firstSheetKey = Object.keys(data)[0];
        const projectData = data[firstSheetKey];

        if (!projectData || !Array.isArray(projectData) || projectData.length === 0) {
          setErrors(prev => ({
            ...prev,
            fileUpload: 'The file appears to be empty or has no data rows.',
          }));
          return;
        }

        // Auto-detect the date range from column headers
        const { dateRange, error: dateError } = autoDetectDateRange(data, getMonthsBetweenDates);
        if (dateError) {
          setErrors(prev => ({ ...prev, fileUpload: dateError }));
          setFileData({ file, data });
          return;
        }
        setDetectedDateRange(dateRange);

        setFileData({
          file: file,
          data: data,
        });

        // Construct projectDetails from the title field + auto-detected dates
        const projectDetails = {
          'Project Title': state.projectTitle,
          'Start Date': dateRange[0],
          'End Date': dateRange[dateRange.length - 1],
        };

        // Run validation automatically
        setIsValidating(true);
        console.log(`[Validation] Starting auto-validation of ${projectData.length.toLocaleString()} rows...`);
        const startTime = performance.now();

        // Creep the bar from 100% back to a "validating" state visually
        setParseProgress(prev => ({ ...prev, percent: 5, message: 'Running data quality checks...' }));
        startCreep(5, 90, 150);

        // Use a timeout to let the UI update before heavy validation
        // 300ms gives the creep interval enough cycles to visibly advance before blocking
        setTimeout(() => {
          try {
            const { validateProjectData: contextValidate, sanitizeProjectData } = context;

            const sanitized = sanitizeProjectData(projectData, projectDetails);
            const validation = contextValidate
              ? contextValidate(sanitized.projectData, sanitized.projectDetails)
              : validateProjectData(sanitized.projectData, sanitized.projectDetails);

            const elapsed = ((performance.now() - startTime) / 1000).toFixed(1);
            console.log(`[Validation] Complete in ${elapsed}s — ${validation.errors.length} errors, ${validation.warnings.length} warnings`);

            // ── Cross-sheet Changes validation (export re-upload only) ───────────
            // If the file has a Changes sheet, check that every GL key referenced
            // in Selected Accounts actually exists in the Account Data sheet.
            // This catches stale references caused by bulk edits to the export.
            if (data['Changes'] && Array.isArray(data['Changes'])) {
              const accountGlKeys = new Set(
                sanitized.projectData
                  .filter(r => r['Entity ID'] && r['Department ID'] && r['Account ID'])
                  .map(r => `${String(r['Entity ID']).trim()}-${String(r['Department ID']).trim()}-${String(r['Account ID']).trim()}`)
              );

              const staleRefs = [];
              data['Changes'].forEach((changeRow, idx) => {
                const label = String(changeRow['Change Label'] ?? '').trim();
                const selectedRaw = String(changeRow['Selected Accounts'] ?? '').trim();
                if (!selectedRaw) return;
                selectedRaw.split(',').map(s => s.trim()).filter(Boolean).forEach(glKey => {
                  if (!accountGlKeys.has(glKey)) {
                    staleRefs.push({ change: label || `Row ${idx + 2}`, glKey });
                  }
                });
              });

              if (staleRefs.length > 0) {
                const uniqueKeys = [...new Set(staleRefs.map(r => r.glKey))];
                const affectedChanges = [...new Set(staleRefs.map(r => r.change))];
                validation.warnings.push({
                  type: 'dataset',
                  message:
                    `${staleRefs.length} GL key reference${staleRefs.length !== 1 ? 's' : ''} in the Changes sheet ` +
                    `could not be found in the Account Data sheet. ` +
                    `Affected change${affectedChanges.length !== 1 ? 's' : ''}: ${affectedChanges.slice(0, 5).join(', ')}${affectedChanges.length > 5 ? ` and ${affectedChanges.length - 5} more` : ''}. ` +
                    `Missing GL key${uniqueKeys.length !== 1 ? 's' : ''}: ${uniqueKeys.slice(0, 5).join(', ')}${uniqueKeys.length > 5 ? ` and ${uniqueKeys.length - 5} more` : ''}. ` +
                    `These changes will be skipped during import. If this is intentional (e.g. accounts were removed), you can proceed.`,
                });
              }
            }
            // ────────────────────────────────────────────────────────────────────

            stopCreep();
            setParseProgress(prev => ({ ...prev, percent: 100, message: `Validation complete — ${validation.errors.length} error${validation.errors.length !== 1 ? 's' : ''}, ${validation.warnings.length} warning${validation.warnings.length !== 1 ? 's' : ''}` }));

            // Filter out info-severity errors (always non-strict)
            const filteredErrors = validation.errors.filter(e => e.severity !== 'info');
            const filteredWarnings = validation.warnings;

            setValidationResults({
              errors: filteredErrors,
              warnings: filteredWarnings,
              infos: validation.infos || [],
              isValid: filteredErrors.length === 0,
            });

            if (filteredErrors.length > 0) {
              setErrors(prev => ({
                ...prev,
                validationErrors: filteredErrors,
                validationWarnings: filteredWarnings,
              }));
            } else if (filteredWarnings.length > 0) {
              setErrors(prev => ({
                ...prev,
                validationWarnings: filteredWarnings,
              }));
            }
          } catch (valErr) {
            console.error('[Validation] Error during validation:', valErr);
            stopCreep();
          } finally {
            setIsValidating(false);
          }
        }, 300);

      } catch (err) {
        console.error('[ExcelParser] Parse failed:', err.message, err);
        setErrors({
          ...errors,
          fileUpload: `Failed to read the file: ${err.message}`,
        });
      }
  };

  const handleError = (file) => {
    console.error('Dropzone: handleError: ', file[0].errors[0].message);
    setErrors({
      ...errors,
      templateUpload: true,
    })
  };

  const downloadErrorReport = (errors) => {
    // Sanitize CSV content to prevent CSV injection
    const sanitizeCell = (value) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      let safe = str;
      // Neutralize dangerous leading characters that could execute as formulas
      if (/^[=+\-@]/.test(safe)) {
        // Prepend a tab so spreadsheets treat this as text, not a formula
        safe = '\t' + safe;
      }
      // Escape embedded double quotes for CSV
      safe = safe.replace(/"/g, '""');
      // Wrap in double quotes to produce a well-formed CSV cell
      return `"${safe}"`;
    };
    
    const csvContent = [
      ['Row', 'Field', 'Error Message'].map(header => sanitizeCell(header)),
      ...errors.map(err => [
        sanitizeCell(err.row || 'N/A'),
        sanitizeCell(err.field || 'General'),
        sanitizeCell(err.message)
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `validation_errors_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildProject = async () => {
    const { validateProjectData: contextValidate, sanitizeProjectData } = context;
    
    // Get the effective organization ID from simulation context
    const effectiveOrganization = simulation.getEffectiveOrganization();
    const effectiveOrgId = effectiveOrganization?.id;

    // Set loading immediately so the UI responds before the synchronous
    // sanitize/format work blocks the main thread
    setIsLoading(true);

    // Guard: title must be entered
    if (!state.projectTitle.trim()) {
      setErrors({
        fileUpload: 'Please enter a project title.',
        templateUpload: false,
        validationErrors: [],
        validationWarnings: [],
      });
      setIsLoading(false);
      return;
    }

    // Validate fileData exists and has required structure
    if (!fileData.data) {
      console.error('[BuildProject] No file data — upload a file first.');
      setErrors({
        fileUpload: 'No file has been uploaded. Please upload a data file first.',
        templateUpload: false,
        validationErrors: [],
        validationWarnings: [],
      });
      setIsLoading(false);
      return;
    }

    // Guard: date range must have been auto-detected
    if (!detectedDateRange || detectedDateRange.length < 2) {
      setErrors({
        fileUpload: 'No valid date range was detected from the uploaded file. Please check your file and try again.',
        templateUpload: false,
        validationErrors: [],
        validationWarnings: [],
      });
      setIsLoading(false);
      return;
    }

    // Use only the first sheet as the Data sheet
    const firstSheetKey = Object.keys(fileData.data)[0];
    let projectData = fileData.data[firstSheetKey];

    if (!projectData || !Array.isArray(projectData) || projectData.length === 0) {
      const msg = 'The file appears to be empty or has no data rows.';
      console.error('[BuildProject]', msg);
      setErrors({
        fileUpload: msg,
        templateUpload: false,
        validationErrors: [],
        validationWarnings: [],
      });
      setIsLoading(false);
      return;
    }

    // Construct Project_Details from the title field + auto-detected dates
    let projectDetails = {
      'Project Title': state.projectTitle,
      'Start Date': detectedDateRange[0],
      'End Date': detectedDateRange[detectedDateRange.length - 1],
    };

    // Clear previous errors
    setErrors({
      fileUpload: null,
      templateUpload: false,
      validationErrors: [],
      validationWarnings: [],
    });

    // Defer the heavy sanitize/format work so the loading state renders first
    await new Promise(resolve => setTimeout(resolve, 50));

    // SANITIZE data first
    const sanitized = sanitizeProjectData(projectData, projectDetails);
    projectData = sanitized.projectData;
    projectDetails = sanitized.projectDetails;

    // THEN validate
    // Use context validation if available, otherwise use imported validation
    const validation = contextValidate 
      ? contextValidate(projectData, projectDetails)
      : validateProjectData(projectData, projectDetails);
    
    // Filter out info-severity errors (always non-strict)
    const displayedWarnings = validation.warnings;

    setValidationResults({
      ...validation,
      warnings: displayedWarnings
    });
    setIsValidating(false);

    // If validation has errors, prevent submission and display them
    if (!validation.isValid) {
      setErrors({
        fileUpload: null,
        templateUpload: false,
        validationErrors: validation.errors,
        validationWarnings: validation.warnings,
      });
      return;
    }

    // If validation has warnings, show them but allow submission
    if (validation.warnings.length > 0) {
      setErrors({
        fileUpload: null,
        templateUpload: false,
        validationErrors: [],
        validationWarnings: validation.warnings,
      });
    }

    // Prepare project data for async API
    // Normalize start/end date from 'MMM yyyy' (e.g. "Jan 2024") to ISO 'yyyy-MM-01' (first of month)
    const REF_DATE = new Date(2000, 0, 1);
    const startDateISO = format(parse(projectDetails['Start Date'], 'MMM yyyy', REF_DATE), 'yyyy-MM-dd');
    const endDateISO = format(parse(projectDetails['End Date'], 'MMM yyyy', REF_DATE), 'yyyy-MM-dd');

    // Get the expected month range in 'MMM yyyy' format for building the dates payload
    const expectedMonths = getMonthsBetweenDates(projectDetails['Start Date'], projectDetails['End Date']) || [];

    const asyncProjectData = {
      project_title: projectDetails['Project Title'],
      start_date: startDateISO,
      end_date: endDateISO,
      organization_id: effectiveOrgId || auth.activeUser?.organization?.id,
    };

    // Show loading state and initiate async project creation
    setIsLoading(true);
    
    try {
      // For large files, upload directly to S3 via presigned URL to avoid ALB 1MB limit.
      // For small files (<500 rows), send accounts directly in the request body.
      const LARGE_FILE_THRESHOLD = 500;
      let finalProjectData = asyncProjectData;

      if (projectData.length > LARGE_FILE_THRESHOLD && fileData.file) {
        // Step 1: Get a presigned S3 upload URL
        const presignResponse = await fetch(
          `${import.meta.env.VITE_MAC_API_URL || import.meta.env.VITE_API_URL}/projects/presign-upload`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${auth.activeUser.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              filename: fileData.file.name,
              content_type: fileData.file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
          }
        );

        if (!presignResponse.ok) {
          throw new Error(`Failed to get upload URL: ${presignResponse.status}`);
        }

        const { upload_url, s3_key } = await presignResponse.json();

        // Step 2: Upload the file directly to S3
        const uploadResponse = await fetch(upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': fileData.file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          body: fileData.file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.status}`);
        }

        // Step 3: Send just the S3 key — no accounts in the body
        finalProjectData = { ...asyncProjectData, s3_key };
      } else {
        // Small dataset — send accounts directly
        finalProjectData = {
          ...asyncProjectData,
          accounts: projectData.map(account => {
            const colMap = {};
            Object.keys(account).forEach(col => { colMap[normalizeDateCol(col)] = col; });
            const dates = expectedMonths.reduce((acc, monthStr) => {
              const actualCol = colMap[monthStr] || monthStr;
              const isoKey = format(parse(monthStr, 'MMM yyyy', REF_DATE), 'yyyy-MM-dd');
              const value = account[actualCol];
              acc[isoKey] = (value === null || value === undefined || value === '') ? 0 : (Number.isFinite(Number(value)) ? Number(value) : 0);
              return acc;
            }, {});
            return {
              entity_id: String(account['Entity ID']), entity: account['Entity'],
              department_id: String(account['Department ID']), department_name: account['Department Name'],
              account_id: String(account['Account ID']), account_name: account['Account Name'],
              parent_account: account['Parent Account'] || '', acct_type: account['Acct Type'],
              fixed_or_variable: account['Fixed or Variable'] || '', driver: account['Driver'] || '',
              dates
            };
          })
        };
      }

      // Call async project creation endpoint
      const response = await createProjectAsync(finalProjectData, auth.activeUser.accessToken);
      
      // Handle case where createProjectAsync returns an Axios error object or a plain Error instead of throwing
      const isErrorLike =
        response &&
        typeof response === 'object' &&
        (response.isAxiosError ||
          (response.response && typeof response.response === 'object' && 'status' in response.response));
      const isPlainError = response instanceof Error;

      if (isPlainError || isErrorLike) {
        if (isPlainError) {
          // Surface the original Error message from createProjectAsync (e.g., validateParams failures)
          throw new Error(response.message || 'Async project creation failed');
        }
        const status = response.response && response.response.status;
        const backendMessage =
          (response.response && response.response.data && response.response.data.message) ||
          response.message;

        throw new Error(
          backendMessage ||
            (status ? `Async project creation failed with status ${status}` : 'Async project creation failed')
        );
      }

      if (response.data && response.data.job_id) {
        if (!isMountedRef.current) return;
        
        setIsLoading(false);

        // If the uploaded file has a Changes sheet, parse it and pass the
        // changes through navigation state so they can be imported after the
        // project creation job completes.
        let pendingChanges = null;
        if (fileData.data && fileData.data['Changes']) {
          const refRows = fileData.data['Reference Accounts'] ?? [];
          const parsedChanges = parseChangesSheet(fileData.data['Changes']);
          pendingChanges = parseReferenceAccountsSheet(refRows, parsedChanges);
        }
        
        history.push('/project-select', {
          newProject: {
            title: projectDetails['Project Title'],
            jobId: response.data.job_id,
            createdOn: new Date().toISOString(),
            createdBy: auth.activeUser.email || auth.activeUser.username,
            pendingChanges,
          }
        });
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (error) {
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      
      setIsLoading(false);
      setSuccessMessage(''); // Clear any success message
      
      setErrors({
        fileUpload: null,
        templateUpload: false,
        validationErrors: [{
          type: 'project',
          message: `Failed to initiate project creation: ${error.message || 'Unknown error'}. Please try again.`
        }],
        validationWarnings: [],
      });
    }
  };

  const handleCancelProject = async () => {
    // Delete the project from backend if it exists
    if (context.activeProjectInfo && context.activeProjectInfo.id) {
      try {
        await context.deleteProject(context.activeProjectInfo.id, auth.activeUser.accessToken);
      } catch (error) {
        // Continue with UI reset even if deletion fails
      }
    }
    
    // Reset all states
    setSuccessMessage('');
    setCreatedProjectTitle('');
    setFileData({
      file: false,
      data: '',
    });
    clearErrors();
    setValidationResults({
      errors: [],
      warnings: [],
      infos: [],
      isValid: true,
    });
  };

  async function checkProjectTitleExistence(projectTitle) {
    // TODO: Implement project title existence check
    return false;
  }

  async function handleProjectTitleChange(projectTitle){
    let { formatFileUploadData } = context;
    
    // Get the effective organization ID from simulation context
    const effectiveOrganization = simulation.getEffectiveOrganization();
    const effectiveOrgId = effectiveOrganization?.id;
    
    const projectTitleExists = await checkProjectTitleExistence(projectTitle)
    // console.log("handleProjectTitleChange projectTitleExists ",projectTitleExists)
    if(projectTitleExists){
      setProjectExists(true)
      setOpenChangeProjectTitleModal(true)
    }else{
      // console.log("não existe")
      setProjectExists(false)
      setOpenChangeProjectTitleModal(false)
      // console.log("projectTitle ",projectTitle)
      formatFileUploadData(fileData.data, auth.activeUser, effectiveOrgId);
      // console.log("formatFileUploadData ",formatFileUploadData)
      history.push('/project-home');
    }
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Navigation title={'New Project'} />
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Box
        component="main"
        sx={{
          backgroundColor: 'background.paper',
          flexGrow: 1,
          minHeight: '100vh',
          overflow: 'auto',
        }}
      >
        <ChangeProjectTitleModal
            openChangeProjectTitleModal={openChangeProjectTitleModal}
            setOpenChangeProjectTitleModal={setOpenChangeProjectTitleModal}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            projectExists = {projectExists}
            setProjectExists = {setProjectExists}
            onConfirm={(value) => handleProjectTitleChange(value)}
          />
        <Toolbar />
        <Box sx={{ mt: 4, mb: 4, px: 3 }}>
          <SimulationBanner />
          <Box component="form" noValidate autoComplete="on">
            <Grid container spacing={3}>

                    {/* ── Page heading row — title left, download button top-right ── */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Create a new project
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Enter a project title, upload your data file, and click Create.
                            Need a blank template? Click "Download blank template" to get one.
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<DownloadIcon />}
                          onClick={() => setTemplateModalOpen(true)}
                          disabled={isLoading}
                          sx={{ ml: 2, flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                          Download blank template
                        </Button>
                      </Box>
                      <TemplateDownloadModal
                        open={templateModalOpen}
                        onClose={() => setTemplateModalOpen(false)}
                      />
                    </Grid>

                    {/* ── Project Title ── */}
                    <Grid item xs={12}>
                      <TextField
                        disabled={isLoading}
                        id="projectTitle"
                        label="Project Title"
                        onChange={handleChange}
                        onBlur={() => setTitleTouched(true)}
                        sx={{ width: '435px' }}
                        variant="outlined"
                        value={state.projectTitle}
                        error={titleError}
                        helperText={titleError ? 'Project title is required' : ''}
                        inputRef={titleFieldRef}
                      />
                    </Grid>

                    {/* ── Dropzone ── */}
                    <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ maxWidth: 720, width: '100%' }}>
                        <Dropzone
                          accept={['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', '.csv']}
                          onDropAccepted={(fileObjs) => handleFileUpload(fileObjs)}
                          onDropRejected={(error) => handleError(error)}
                          maxFiles={1}
                          multiple={false}
                          disabled={isParsing}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <section style={{ width: '100%' }}>
                              <div {...getRootProps({ className: 'file-dropzone' })}>
                                <input {...getInputProps()} />
                                <h3><DriveFolderUploadIcon fontSize="large" /> Drag and drop here, or click upload</h3>
                                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#666' }}>Accepts .xlsx, .xls, or .csv</p>
                              </div>
                            </section>
                          )}
                        </Dropzone>
                      </Box>

                      {/* Parse / validation progress bar */}
                      {(isParsing || isValidating) && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CircularProgress size={20} sx={{ mr: 1.5 }} />
                            <Typography variant="body2" color="text.secondary">
                              {isParsing ? (parseProgress.message || 'Processing file...') : 'Running data quality checks...'}
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                            <Box sx={{ width: isParsing ? `${parseProgress.percent}%` : '100%', bgcolor: 'primary.main', borderRadius: 1, height: 8, transition: 'width 0.3s ease' }} />
                          </Box>
                        </Box>
                      )}

                      {/* Uploaded file name */}
                      {fileData.file && (
                        <Typography variant="body2" sx={{ mt: 1, ml: 0.5 }}>
                          {fileData.file.path || fileData.file.name} — {fileData.file.size.toLocaleString()} bytes
                        </Typography>
                      )}

                      {/* Detected date range confirmation */}
                      {detectedDateRange.length >= 2 && (
                        <Box sx={{ mt: 1.5, alignSelf: 'flex-start', maxWidth: 720 }}>
                          <Box sx={{
                            border: '1.5px solid',
                            borderColor: 'success.main',
                            borderRadius: 2,
                            px: 2,
                            py: 1.25,
                            bgcolor: 'success.50',
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                              ✓ Detected date range: {detectedDateRange[0]} – {detectedDateRange[detectedDateRange.length - 1]} ({detectedDateRange.length} months)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              If this range doesn't look right, check your file's column headers. Date columns should follow the format <strong>Jan 2024</strong>, <strong>January 2024</strong>, <strong>01/2024</strong>, or <strong>2024-01-01</strong>, and must appear after the 10 required account columns (Entity ID, Entity, Department ID, Department Name, Account ID, Account Name, Parent Account, Acct Type, Fixed or Variable, Driver).
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Changes sheet detected */}
                      {fileData.data && fileData.data['Changes'] && (
                        <Box sx={{ mt: 1.5, alignSelf: 'flex-start', maxWidth: 720 }}>
                          <Box sx={{
                            border: '1.5px solid',
                            borderColor: 'info.main',
                            borderRadius: 2,
                            px: 2,
                            py: 1.25,
                            bgcolor: 'info.50',
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.dark' }}>
                              ✓ Changes detected ({fileData.data['Changes'].length} change{fileData.data['Changes'].length !== 1 ? 's' : ''})
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              This file was exported from BridgeFirst. All changes will be automatically imported after the project is created.
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* File upload / date detection errors */}
                      {errors.fileUpload && (
                        <Alert severity="error" onClose={() => setErrors({ ...errors, fileUpload: null })} sx={{ mt: 2, mb: 2 }}>
                          <AlertTitle>File Upload Error</AlertTitle>
                          <Typography variant="body2">{errors.fileUpload}</Typography>
                        </Alert>
                      )}

                      {errors.templateUpload && (
                        <Alert severity="error" onClose={() => setErrors({ ...errors, templateUpload: false })} sx={{ mt: 2, mb: 2 }}>
                          <AlertTitle>Invalid File Type</AlertTitle>
                          Please upload an Excel file (.xlsx or .xls) or a CSV file (.csv).
                        </Alert>
                      )}

                      {/* Data quality passed */}
                      {hasPassedDataQuality && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                          <AlertTitle><strong>Data Quality Check Passed</strong></AlertTitle>
                          <Typography variant="body2">All data sanitized and validated successfully</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                            • Whitespace trimmed from all text fields<br />
                            • Enum values normalized<br />
                            • No formula injection detected<br />
                            • All date columns present<br />
                            • Cross-row consistency verified
                          </Typography>
                        </Alert>
                      )}

                    </Grid>
                    {(filteredErrors.dataset.length > 0 || filteredErrors.relationship.length > 0 || filteredErrors.consistency.length > 0 || filteredErrors.general.length > 0 || (errors.validationWarnings && errors.validationWarnings.length > 0)) && (
                      <Grid item xs={12}>
                        {filteredErrors.dataset.length > 0 && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle><strong>Dataset Validation Failed</strong></AlertTitle>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {filteredErrors.dataset.map((err, idx) => (
                                <li key={idx} style={{ marginBottom: 8 }}>{err.message}</li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {filteredErrors.relationship.length > 0 && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle><strong>Relationship Validation Errors</strong></AlertTitle>
                            <Typography variant="body2" sx={{ mb: 1 }}>The following account relationships are invalid:</Typography>
                            <Box sx={{ maxHeight: 300, overflow: 'auto', backgroundColor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 1 }}>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {(expandedErrorSections.relationship ? filteredErrors.relationship : filteredErrors.relationship.slice(0, MAX_DISPLAYED_ERRORS)).map((err, idx) => (
                                  <li key={idx} style={{ marginBottom: 8 }}><strong>Row {err.row}</strong> - <em>{err.field}</em>: {err.message}</li>
                                ))}
                              </ul>
                              {filteredErrors.relationship.length > MAX_DISPLAYED_ERRORS && (
                                <Button size="small" onClick={() => toggleErrorSection('relationship')} startIcon={expandedErrorSections.relationship ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mt: 1 }}>
                                  {expandedErrorSections.relationship ? 'Show Less' : `Show ${filteredErrors.relationship.length - MAX_DISPLAYED_ERRORS} More Error${filteredErrors.relationship.length - MAX_DISPLAYED_ERRORS !== 1 ? 's' : ''}`}
                                </Button>
                              )}
                            </Box>
                          </Alert>
                        )}

                        {filteredErrors.consistency && filteredErrors.consistency.length > 0 && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle><strong>Data Consistency Errors</strong></AlertTitle>
                            <Typography variant="body2" sx={{ mb: 1 }}>The same ID must always have the same name throughout your file:</Typography>
                            <Box sx={{ maxHeight: 300, overflow: 'auto', backgroundColor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 1 }}>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {(expandedErrorSections.consistency ? filteredErrors.consistency : filteredErrors.consistency.slice(0, MAX_DISPLAYED_ERRORS)).map((err, idx) => (
                                  <li key={idx} style={{ marginBottom: 8 }}><strong>Row {err.row}</strong> - <em>{err.field}</em>: {err.message}</li>
                                ))}
                              </ul>
                              {filteredErrors.consistency.length > MAX_DISPLAYED_ERRORS && (
                                <Button size="small" onClick={() => toggleErrorSection('consistency')} startIcon={expandedErrorSections.consistency ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mt: 1 }}>
                                  {expandedErrorSections.consistency ? 'Show Less' : `Show ${filteredErrors.consistency.length - MAX_DISPLAYED_ERRORS} More Error${filteredErrors.consistency.length - MAX_DISPLAYED_ERRORS !== 1 ? 's' : ''}`}
                                </Button>
                              )}
                            </Box>
                          </Alert>
                        )}

                        {filteredErrors.general.length > 0 && filteredErrors.dataset.length === 0 && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle><strong>Upload Validation Failed</strong> — {filteredErrors.general.length} error{filteredErrors.general.length !== 1 ? 's' : ''} found</AlertTitle>
                            <Typography variant="body2" sx={{ mb: 1 }}>Please fix the following issues and re-upload your file:</Typography>
                            <Box sx={{ maxHeight: 300, overflow: 'auto', backgroundColor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 1 }}>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {(expandedErrorSections.general ? filteredErrors.general : filteredErrors.general.slice(0, MAX_DISPLAYED_ERRORS)).map((err, idx) => (
                                  <li key={idx} style={{ marginBottom: 8 }}>
                                    {err.row && <strong>Row {err.row}</strong>}
                                    {err.field && <> - <em>{err.field}</em>:</>}
                                    {' '}{err.message}
                                  </li>
                                ))}
                              </ul>
                              {filteredErrors.general.length > MAX_DISPLAYED_ERRORS && (
                                <Button size="small" onClick={() => toggleErrorSection('general')} startIcon={expandedErrorSections.general ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mt: 1 }}>
                                  {expandedErrorSections.general ? 'Show Less' : `Show ${filteredErrors.general.length - MAX_DISPLAYED_ERRORS} More Error${filteredErrors.general.length - MAX_DISPLAYED_ERRORS !== 1 ? 's' : ''}`}
                                </Button>
                              )}
                            </Box>
                            <Button variant="contained" color="error" size="small" startIcon={<DownloadIcon />} onClick={() => downloadErrorReport(errors.validationErrors)} sx={{ mt: 2 }}>
                              Download Error Report
                            </Button>
                          </Alert>
                        )}

                        {errors.validationWarnings && errors.validationWarnings.length > 0 && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            <AlertTitle><strong>{errors.validationWarnings.length} Warning{errors.validationWarnings.length !== 1 ? 's' : ''} Detected</strong></AlertTitle>
                            <Typography variant="body2" sx={{ mb: 1 }}>The following issues were found but won't prevent upload:</Typography>
                            <Button size="small" onClick={() => setShowWarnings(!showWarnings)} sx={{ mb: 1 }}>
                              {showWarnings ? 'Hide' : 'Show'} Warnings
                            </Button>
                            <Collapse in={showWarnings}>
                              <Box sx={{ maxHeight: 200, overflow: 'auto', backgroundColor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 1 }}>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {errors.validationWarnings.map((warn, idx) => (
                                    <li key={idx} style={{ marginBottom: 8 }}>
                                      {warn.row && <strong>Row {warn.row}</strong>}
                                      {warn.field && <> - <em>{warn.field}</em>:</>}
                                      {' '}{warn.message}
                                    </li>
                                  ))}
                                </ul>
                              </Box>
                            </Collapse>
                          </Alert>
                        )}
                      </Grid>
                    )}

                    {/* ── Create button + requirements hint ── */}
                    <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 4 }}>
                      {(() => {
                        const hintParts = [];
                        if (!state.projectTitle.trim()) hintParts.push('a project title');
                        if (!fileData.file) hintParts.push('a data file');
                        return (
                          <>
                            {hintParts.length > 0 && (
                              <Alert
                                severity="info"
                                sx={{ mb: 1.5, maxWidth: 500 }}
                              >
                                To create a project, please provide: <strong>{hintParts.join(' and ')}</strong>.
                              </Alert>
                            )}
                            <Button
                              variant="contained"
                              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                              color="success"
                              size="large"
                              disabled={
                                !fileData.file ||
                                isLoading ||
                                isParsing ||
                                isValidating ||
                                errors.validationErrors.length > 0 ||
                                !!errors.fileUpload ||
                                !state.projectTitle.trim()
                              }
                              sx={{ mt: 0 }}
                              onClick={() => buildProject()}
                            >
                              {isLoading ? 'Preparing project...' : 'Create new project'}
                            </Button>
                          </>
                        );
                      })()}
                    </Grid>

                  </Grid>
                </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default NewProject;

// Named exports for unit/property testing
export { normalizeDateCol, autoDetectDateRange };
